import React, { MouseEventHandler, useState } from 'react';
import Separator from '@westwing/ui-kit/Separator';
import { isMobile } from '@westwing/ui-kit/breakPoints';
import ActionButton from '@westwing/ui-kit/ActionButton';
import styled from 'styled-components';
import { body2 } from '@westwing/ui-kit/typography';
import {
    StyledModalTitle,
    StyledHeaderCentered,
    StyledFieldset,
    StyledModalParagraph,
    StyledModalContent,
} from '../loginAndRegistration.style';
import useTranslate from 'Hooks/useTranslate';
import { useConfig } from 'AppShell/hooks';

type AccountTransferConfirmProps = {
    email: string;
    onAccept: MouseEventHandler<HTMLButtonElement>;
    onDeny: MouseEventHandler<HTMLButtonElement>;
};
const StyledContainer = styled(StyledFieldset)`
    padding-bottom: 8px;
`;

const StyledLegal = styled.small`
    ${body2}
    font-size: 10px;
    line-height: 1.6;
    text-align: justify;
    color: ${({ theme }) => theme.colors.charcoal80};
    span {
        display: inline-block;
        text-align: inherit;
    }
`;
const StyledDangerDisclaimer = styled.small`
    ${body2}
    font-size: 12px;
    line-height: 1.6;
    color: ${({ theme }) => theme.colors.iconCritical};
    padding: 2px 0;
`;
const StyledParagraphGroup = styled.div`
    p {
        margin: 0;
    }
    p + p {
        margin-top: 6px;
    }
`;
const AccountTransferConfirm = ({ onDeny, onAccept, email, ...props }: AccountTransferConfirmProps): JSX.Element => {
    const { tPlain, t } = useTranslate();
    const [deny, setDeny] = useState(false);
    const {
        links: { dataProtection },
    } = useConfig();
    const headline = isMobile() ? tPlain('datapolicy_header_mobile_bella') : tPlain('datapolicy_header_bella');
    const description = tPlain('datapolicy_login_text_bella', [email]);
    const acceptCTA = tPlain('datapolicy_accept_bella');
    const denyCTA = tPlain('datapolicy_back_bella');
    const footnote = t('datapolicy_footnote_bella', [
        `<a href="${dataProtection}" target="_blank">`,
        '</a>',
        `<a href="${dataProtection}" target="_blank">`,
        '</a>',
    ]);
    const warning = isMobile()
        ? tPlain('datapolicy_are_you_sure_mobile_bella')
        : tPlain('datapolicy_are_you_sure_bella');

    const denyBlock = () =>
        !deny ? (
            <ActionButton data-testid="mf-login-btn-deny" type="button" kind="secondary" onClick={() => setDeny(true)}>
                {denyCTA}
            </ActionButton>
        ) : (
            <div>
                <ActionButton
                    data-testid="mf-login-btn-deny-confirm"
                    type="button"
                    aria-label="confirm"
                    kind="danger"
                    onClick={onDeny}
                >
                    {denyCTA}
                </ActionButton>
                <StyledDangerDisclaimer>{warning}</StyledDangerDisclaimer>
            </div>
        );

    return (
        <div {...props}>
            <StyledHeaderCentered>
                <StyledModalTitle>{headline}</StyledModalTitle>
            </StyledHeaderCentered>
            <StyledModalContent>
                <StyledContainer as="div">
                    <StyledParagraphGroup>
                        <StyledModalParagraph as="div" dangerouslySetInnerHTML={{ __html: description }} />
                    </StyledParagraphGroup>
                    <ActionButton data-testid="mf-login-btn-accept" type="button" kind="primary" onClick={onAccept}>
                        {acceptCTA}
                    </ActionButton>
                    {denyBlock()}
                </StyledContainer>

                <Separator />

                <StyledLegal>{footnote}</StyledLegal>
            </StyledModalContent>
        </div>
    );
};
export default AccountTransferConfirm;
